import React, { useEffect, useRef } from 'react';
import Card from '../../components/common/FeatureCard';

const FeaturesList = () => {
  const features = [
    {
      id: 1,
      section: 'interactive',
      title: 'Interactive Digital Filter Designer',
      description:
        "Shanon Technologies' graphical FIR/IIR digital filter designer offers an intuitive interface for easy and efficient filter design. With this tool, you can specify your requirements graphically and achieve quick results.",
      image: '/images/image3.webp',
      cards: [
        {
          id: 'f1',
          icon: '/images/img_checkcircle.webp',
          title: 'Powerful DSP Platform',
          description: 'Design, validate, and deploy digital filters in minutes.',
        },
        {
          id: 'f2',
          icon: '/images/img_checkcircle.webp',
          title: 'Real-Time Feedback',
          description:
            'Draw and adjust requirements interactively without predefined specifications.',
        },
        {
          id: 'f3',
          icon: '/images/img_checkcircle.webp',
          title: 'Versatile Arithmetic Options',
          description:
            'Supports double precision, single precision, and fixed-point arithmetic (8-32 bits) for embedded applications.',
        },
        {
          id: 'f4',
          icon: '/images/img_checkcircle.webp',
          title: 'Interactive Phase Equalization',
          description: 'Equalize phase responses using an all-pass filter cascade.',
        },
        {
          id: 'f5',
          icon: '/images/img_checkcircle.webp',
          title: 'Interactive Pole-Zero Editor',
          description:
            'Zoom, pan, and fine-tune designs graphically with real-time frequency response updates.',
        },
      ],
    },
    {
      id: 2,
      section: 'code',
      title: 'Effortless and Editable Code Generation',
      description:
        'At Shanon Technologies, we prioritize generating code that is clean, readable, and easily modifiable. Our solution ensures that the code is not only efficient but also intuitive, allowing developers to quickly understand and adjust it as needed.',
      image: '/images/image5.webp',
      cards: [
        {
          id: 'c1',
          icon: '/images/img_checkcircle.webp',
          title: 'Readability',
          description:
            'Clear and consistent organization using logical structuring and naming conventions.',
        },
        {
          id: 'c2',
          icon: '/images/img_checkcircle.webp',
          title: 'Modularity',
          description:
            'Code is divided into distinct modules, enabling targeted modifications without affecting the entire system.',
        },
        {
          id: 'c3',
          icon: '/images/img_checkcircle.webp',
          title: 'Simplicity',
          description:
            'Direct and essential code, reducing the time required for revisions and adjustments.',
        },
        {
          id: 'c4',
          icon: '/images/img_checkcircle.webp',
          title: 'Customizability',
          description:
            'Easily adjustable to meet specific project needs, with annotated sections indicating where changes can be made.',
        },
      ],
      reversed: true,
    },
    {
      id: 3,
      section: 'autoadapt',
      title: 'AutoAdapt',
      description:
        "Shanon Technologies' ST-Bridge automates repetitive tasks and internal discretization, simplifying design modifications. Whether changing a filter or adjusting PID coefficients, ST-Bridge makes it easy and updates transfer function coefficients automatically with changes in sampling frequency.",
      image: '/images/image6.webp',
      cards: [
        {
          id: 'a1',
          icon: '/images/img_checkcircle.webp',
          title: 'Automatic Discretization',
          description:
            'Automatically applies discretization, saving you from manual calculations and adjustments.',
        },
        {
          id: 'a2',
          icon: '/images/img_checkcircle.webp',
          title: 'Filter Changes',
          description:
            'Easily modify filters without needing to rewrite extensive portions of your code.',
        },
        {
          id: 'a3',
          icon: '/images/img_checkcircle.webp',
          title: 'Sampling Frequency Adaptation',
          description:
            'Automatically updates transfer function coefficients when the sampling frequency changes.',
        },
      ],
    },
    {
      id: 4,
      section: 'monitoring',
      title: 'Real time Monitoring',
      description:
        'With Real-Time Monitoring, you can directly observe the outputs of your design within the embedded system. This feature enables immediate testing and validation of your solution, allowing for real-time adjustments as needed.',
      image: '/images/image7.webp',
      checkItems: [
        'Direct system integration for immediate feedback',
        'Enables quick validation and fine-tuning',
        'Real-time observation of system outputs',
        'Streamlines the testing and adjustment process',
      ],
      reversed: true,
    },
    {
      id: 5,
      section: 'board',
      title: 'Versatile electronic board',
      description:
        'With Real-Time Monitoring, you can directly observe the outputs of your design within the embedded system. This feature enables immediate testing and validation of your solution, allowing for real-time adjustments as needed.',
      image: '/images/shannon-card.webp',
      checkItems: [
        'Direct system integration for immediate feedback',
        'Enables quick validation and fine-tuning',
        'Real-time observation of system outputs',
        'Streamlines the testing and adjustment process',
      ],
    },
    {
      id: 6,
      section: 'prototyping',
      title: 'Rapid Prototyping',
      description:
        'Rapid Prototyping accelerates the development process by allowing quick iterations and testing of your designs. This feature ensures that you can swiftly move from concept to functional prototype, enabling faster validation and refinement of your solutions.',
      image: '/images/image8.webp',
      checkItems: [
        'Accelerates the development cycle ',
        'Enables quick iterations and testing ',
        'Swiftly move from concept to functional prototype ',
        'Facilitates faster validation and refinement',
      ],
      reversed: true,
    },
  ];
  const imageRefs = useRef([]);

 useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fadeInLeft', 'opacity-100');
            if (entry.target.dataset.reversed === 'true') {
              entry.target.classList.remove('animate-fadeInLeft');
              entry.target.classList.add('animate-fadeInRight');
            }
            observer.unobserve(entry.target); // run once
          }
        });
      },
      {
        threshold: 0.3,
      }
    );

    imageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
     <div className="bg-white">
      {features.map((feature, index) => (
        <section
          key={feature.id}
          id={feature.section}
          className={`py-16 lg:py-24 container mx-auto px-6 lg:px-28 ${
            feature.id > 1 ? 'border-t border-[#f0f0f0]' : ''
          }`}
        >
          {[1, 2, 3].includes(feature.id) && (
            <div className="mb-16 lg:mb-24">
              <h2 className="text-xl md:text-2xl lg:text-[36px] font-semibold text-[#121725] leading-snug lg:leading-[44px] mb-4 lg:mb-6 text-center">
                {feature.title}
              </h2>
              <p className="text-base md:text-[16px] lg:text-[20px] text-[#202a44] leading-relaxed lg:leading-[30px] text-center">
                {feature.description}
              </p>
            </div>
          )}

          <div
            className={`flex flex-col lg:flex-row ${
              feature.reversed ? 'lg:flex-row-reverse' : ''
            } items-center gap-12 lg:gap-16`}
          >
            <div
              className={`w-full lg:w-1/2 ${
                feature.reversed ? 'lg:pl-16' : 'lg:pr-16'
              }`}
            >
              {!([1, 2, 3].includes(feature.id)) && (
                <>
                  <h2
                    className={`text-xl md:text-2xl lg:text-[36px] font-semibold text-[#121725] leading-snug lg:leading-[44px] mb-4 lg:mb-6 ${
                      feature.id === 3 ? 'text-center' : ''
                    }`}
                  >
                    {feature.title}
                  </h2>
                  <p className="text-base md:text-[16px] lg:text-[20px] text-[#202a44] leading-relaxed lg:leading-[30px] mb-8 lg:mb-12">
                    {feature.description}
                  </p>
                </>
              )}

              {feature.cards && (
                <div className="space-y-8 lg:space-y-12">
                  {feature.cards.map((card) => (
                    <Card
                      key={card.id}
                      icon={card.icon}
                      title={card.title}
                      description={card.description}
                    />
                  ))}
                </div>
              )}

              {feature.checkItems && (
                <div className="space-y-4 lg:space-y-6 mt-8 lg:mt-12">
                  {feature.checkItems.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <img
                        src="/images/img_checkcircle.webp"
                        alt="check"
                        className="h-6 w-6 lg:h-7 lg:w-7 mt-1"
                      />
                      <p className="ml-3 lg:ml-4 text-sm lg:text-[18px] text-[#202a44] leading-[22px]">
                        {item}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className="w-full lg:w-1/2 opacity-0"
              ref={(el) => (imageRefs.current[index] = el)}
              data-reversed={feature.reversed ? 'true' : 'false'}
            >
              <img
                src={feature.image}
                alt={feature.title}
                className="w-full max-w-md lg:max-w-[620px] h-auto rounded-2xl object-contain mx-auto"
              />
            </div>
          </div>
        </section>
      ))}
    </div>
  );
};

export default FeaturesList;
